import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { cn } from './cn';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  labelHidden?: boolean;
  options: SelectOption[];
}

/**
 * A native `<select>`, deliberately. A custom listbox would cost a keyboard
 * implementation, a focus trap and a mobile story, and buy nothing the search filters in
 * Phase 10 need.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, labelHidden = false, options, className, ...props },
  ref,
) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className={cn(
          'text-xs font-bold uppercase tracking-wide text-ink',
          labelHidden && 'sr-only',
        )}
      >
        {label}
      </label>
      <select
        ref={ref}
        id={id}
        className={cn(
          'rounded-md border border-line bg-white px-4 py-3 text-md text-ink outline-none transition focus:border-red',
          className,
        )}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
});
